/**
 * Buildings Cost Layer — incremental costs vs BAU.
 * Sits on top of physical pipeline: BAU → efficiency → electrification → RNG → grid → emissions.
 * Costs are downstream only; they do not affect physical outcomes.
 *
 * Placeholder policy-analysis assumptions (fuel prices, capital costs, financial) are centralized
 * here and can be refined later with real data or scenario-specific inputs.
 *
 * Simplifications (v1):
 * - Demand response: annual kWh reduction only, not full load-shape economics.
 * - Commercial growth: same simplified growth as efficiency model if no series.
 * - Other/unmodeled efficiency cost: simple proxy ($10k per % point).
 * - Retail electricity and gas prices: flat real values (no time series).
 * - Avoided BAU fossil replacement cost: zero (TODO).
 * - Panel upgrade: not applied in capex by default; can be applied to a share of electrification cases if needed.
 */

import type { PolicyScenario } from './policyImpactService';
import type { BaselineProjections } from './policyBaselineCache';
import type { TimeSeriesEmissionData } from './policyTimeSeriesService';
import { getBuildingEnergyTimeSeries } from './policyTimeSeriesService';
import type { EfficiencyLayerOptions } from './buildingsEfficiencyModel';
import { DEMAND_RESPONSE_CAP_PERCENT, COMMERCIAL_ANNUAL_GROWTH_RATE } from './buildingsEfficiencyModel';
import type { BuildingsBauScenario, BuildingsBauPathway } from './buildingsBauPathways';
import { getDefaultEnergyEfficiencyComponents, hasAnyEfficiencyComponent } from '../types/policyImpact';
import {
  computeBuildingsCostBetweenPathways,
  type BuildingsPathway,
} from './pathwayCostModel';

// ============== ASSUMPTIONS (placeholder; document simplifications) ==============
/**
 * Placeholder policy-analysis assumptions. Refine with real data or scenario-specific inputs.
 * Documented simplifications: flat real prices, single capital values, no avoided BAU replacement (TODO).
 */

// ----- Fuel prices (flat real $; simplification: no time series or location-specific) -----
// Heat pump conversion: electricityAdded_kwh = thermsReplaced * (29.3 / COP). At 40¢/kWh and COP 3, 1 therm → 29.3/3 ≈ 9.77 kWh → $3.91 vs $2/therm gas → electrification increases operating cost.
export const ELECTRICITY_PRICE_PER_KWH = 0.40; // 40 cents per kWh
export const NATURAL_GAS_PRICE_PER_THERM = 2; // $ per therm

// ----- Capital costs (incremental or program; simplification: single values) -----
export const WEATHERIZATION_COST_PER_PARTICIPATING_HOME = 8000;
export const RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT = 5000;
export const COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT = 15000;
/** Residential heat pump cost per converted unit ($). Capex = converted_units × this. */
export const RES_HEAT_PUMP_COST_PER_UNIT = 10000;
/** Commercial heat pump cost per converted equivalent unit ($). Capex = converted_units × this. */
export const COM_HEAT_PUMP_COST_PER_UNIT = 50000;
/** Avg therms per residential unit per year (for commercial equivalent-unit proxy). */
export const AVG_THERMS_PER_RES_UNIT = 400;
/** Avg therms per commercial equivalent unit per year. */
export const AVG_THERMS_PER_COM_EQUIVALENT_UNIT = 3000;
/** Default heat pump COP for therm→kWh conversion: kWh_per_therm = 29.3 / COP. */
export const DEFAULT_HEAT_PUMP_COP = 3.0;
/** Panel upgrade cost; applied to a share of electrification cases if needed (simplification). */
export const PANEL_UPGRADE_COST = 3000;
/** Demand response: annual program cost per % of building kWh reduced (simplified; not full load-shape). */
export const DEMAND_RESPONSE_PROGRAM_COST_PER_PERCENT_KWH_REDUCED = 50000;

// ----- Financial -----
export const DISCOUNT_RATE = 0.03;
export const ANALYSIS_YEARS = 23; // 2023–2045
export const DEFAULT_ASSET_LIFE_YEARS = 20;

// ----- Incentives (reduce net capex) -----
export const HEAT_PUMP_REBATE = 2000;
export const WEATHERIZATION_REBATE = 2000;
export const REACH_CODE_REBATE = 0;
export const DR_INCENTIVE = 0;

export interface BuildingsCostAssumptions {
  electricityPricePerKwh?: number;
  naturalGasPricePerTherm?: number;
  weatherizationCostPerHome?: number;
  resReachIncrementalCostPerUnit?: number;
  comReachIncrementalCostPerUnit?: number;
  resHeatPumpIncrementalCostPerUnit?: number;
  comHeatPumpIncrementalCostPerUnit?: number;
  avgThermsPerResUnit?: number;
  avgThermsPerComEquivalentUnit?: number;
  panelUpgradeCost?: number;
  demandResponseCostPerPercentKwh?: number;
  discountRate?: number;
  heatPumpRebate?: number;
  weatherizationRebate?: number;
}

export function getAssumptions(overrides?: BuildingsCostAssumptions) {
  return {
    electricityPricePerKwh: overrides?.electricityPricePerKwh ?? ELECTRICITY_PRICE_PER_KWH,
    naturalGasPricePerTherm: overrides?.naturalGasPricePerTherm ?? NATURAL_GAS_PRICE_PER_THERM,
    weatherizationCostPerHome: overrides?.weatherizationCostPerHome ?? WEATHERIZATION_COST_PER_PARTICIPATING_HOME,
    resReachIncrementalCostPerUnit: overrides?.resReachIncrementalCostPerUnit ?? RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT,
    comReachIncrementalCostPerUnit: overrides?.comReachIncrementalCostPerUnit ?? COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT,
    resHeatPumpIncrementalCostPerUnit: overrides?.resHeatPumpIncrementalCostPerUnit ?? RES_HEAT_PUMP_COST_PER_UNIT,
    comHeatPumpIncrementalCostPerUnit: overrides?.comHeatPumpIncrementalCostPerUnit ?? COM_HEAT_PUMP_COST_PER_UNIT,
    avgThermsPerResUnit: overrides?.avgThermsPerResUnit ?? AVG_THERMS_PER_RES_UNIT,
    avgThermsPerComEquivalentUnit: overrides?.avgThermsPerComEquivalentUnit ?? AVG_THERMS_PER_COM_EQUIVALENT_UNIT,
    panelUpgradeCost: overrides?.panelUpgradeCost ?? PANEL_UPGRADE_COST,
    demandResponseCostPerPercentKwh: overrides?.demandResponseCostPerPercentKwh ?? DEMAND_RESPONSE_PROGRAM_COST_PER_PERCENT_KWH_REDUCED,
    discountRate: overrides?.discountRate ?? DISCOUNT_RATE,
    heatPumpRebate: overrides?.heatPumpRebate ?? HEAT_PUMP_REBATE,
    weatherizationRebate: overrides?.weatherizationRebate ?? WEATHERIZATION_REBATE,
  };
}

/** Annual series: year and value. */
export interface AnnualSeriesRow {
  year: number;
  value: number;
}

/** Cost breakdown by policy (cumulative or annual as noted). */
export interface CostBreakdownByPolicy {
  weatherization: number;
  residentialReach: number;
  commercialReach: number;
  demandResponse: number;
  electrification: number;
  other: number;
  incentives: number; // negative (rebates reduce net cost)
}

/** Cost-effectiveness metrics. */
export interface CostEffectivenessMetrics {
  upfrontCapitalCost: number;
  incrementalCapitalVsBau: number;
  netAnnualOperatingSavings2045: number;
  cumulativeNetCost: number;
  netPresentValue: number;
  /** Undiscounted cumulative net cost / total building MT CO2e reduced; null if no reduction. */
  costPerTonCO2eReduced: number | null;
  /** First calendar year when cumulative net cost ≤ 0; null if no breakeven in analysis period. */
  paybackYear: number | null;
  /** Years from baseline to payback (paybackYear - BASE_YEAR); null if no payback. Kept for compatibility. */
  paybackPeriodYears: number | null;
}

export interface BuildingsCostResults {
  annualCostSeries: AnnualSeriesRow[];
  annualSavingsSeries: AnnualSeriesRow[];
  annualNetCashFlowSeries: AnnualSeriesRow[];
  cumulativeNetCostSeries: AnnualSeriesRow[];
  costBreakdownByPolicy: CostBreakdownByPolicy;
  costEffectivenessMetrics: CostEffectivenessMetrics;
  /** Total building emissions reduction (MT CO₂e) over analysis period (baseline − policy). */
  totalBuildingEmissionsReductionMT: number;
  /** BAU vs policy utility bills by year (for charts). */
  bauBillByYear: AnnualSeriesRow[];
  policyBillByYear: AnnualSeriesRow[];
  /** BAU vs scenario electricity and natural gas cost by year (for fuel comparison chart). */
  bauGasCostByYear: AnnualSeriesRow[];
  bauElecCostByYear: AnnualSeriesRow[];
  policyGasCostByYear: AnnualSeriesRow[];
  policyElecCostByYear: AnnualSeriesRow[];
  /** Annual capital cost by year (for charts). */
  annualCapitalCostSeries: AnnualSeriesRow[];
  /** Debug/validation: 2045 snapshot. */
  debug?: {
    year2045: {
      bauBill: number;
      policyBill: number;
      annualSavings: number;
      annualCapex: number;
      cumulativeNetCost: number;
      costPerTon: number | null;
      paybackYear: number | null;
    };
  };
  /** Electrification $/unit debug (per-year + 2045 totals) when location is Davis. For validation. */
  electrificationDebug?: import('./pathwayCostModel').BuildingsPathwayCostResult['electrificationDebug'];
  /** Operating cost audit: baseline gas, policy gas/elec, incremental cost, electricity added, $/therm electric. */
  operatingCostDebug?: import('./pathwayCostModel').BuildingsPathwayCostResult['operatingCostDebug'];
}

const BASE_YEAR = 2023;
const TARGET_YEAR = 2045;

function ramp(year: number): number {
  if (year <= BASE_YEAR) return 0;
  if (year >= TARGET_YEAR) return 1;
  return (year - BASE_YEAR) / (TARGET_YEAR - BASE_YEAR);
}

/** Existing residential share in year t (0–1). Simplified fallback when no housing data. */
function defaultExistingResShare(year: number): number {
  if (year <= BASE_YEAR) return 1;
  const growth = 0.02;
  const unitsAtYear = Math.pow(1 + growth, year - BASE_YEAR);
  return Math.max(0, Math.min(1, 1 / unitsAtYear));
}

/** New residential share in year t (1 - existing). Used as proxy for new units. */
function newResShare(year: number, options?: EfficiencyLayerOptions): number {
  const existing = options?.existingResShareByYear?.(year) ?? defaultExistingResShare(year);
  return 1 - existing;
}

/** Commercial: simplified growth (same as efficiency model). New commercial share proxy. */
function newComShare(year: number): number {
  if (year <= BASE_YEAR) return 0;
  const stockIndex = Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, year - BASE_YEAR);
  return Math.max(0, 1 - 1 / stockIndex);
}

export interface ComputeBuildingsCostResultsParams {
  location: string;
  baseline: BaselineProjections;
  policyScenario: PolicyScenario;
  buildingsBauScenario: BuildingsBauScenario;
  buildingsRngPercentOverride?: number;
  buildingsCop?: number;
  efficiencyLayerOptions?: EfficiencyLayerOptions;
  /** Baseline and projected time series (for building emissions and reduction). */
  emissionsResults: {
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  };
  assumptions?: BuildingsCostAssumptions;
  /** When set, compare costs vs this pathway (e.g. flat baseline) instead of BAU. */
  referencePathway?: BuildingsBauPathway;
  /** When referencePathway is set: compare to BAU (state policy) or to policy (state + local). */
  comparisonTarget?: 'bau' | 'policy';
  /** Residential stock proxy by year for electrification CAPEX ($/unit). When omitted, derived from population/2.5. */
  housingUnitsByYear?: Record<number, number>;
}

/**
 * Build BuildingsPathway from therms/kwh arrays and optional reach %.
 */
function toBuildingsPathway(
  years: number[],
  thermsByYear: number[],
  kwhByYear: number[],
  resReachPercent = 0,
  comReachPercent = 0
): BuildingsPathway {
  return {
    years,
    thermsByYear,
    kwhByYear,
    resReachCodePercent: resReachPercent,
    comReachCodePercent: comReachPercent,
  };
}

/** Build zeroed buildings cost result for given years (used when no local policy). */
function zeroedBuildingsCostResult(years: number[]): BuildingsCostResults {
  const zeroSeries = years.map((y) => ({ year: y, value: 0 }));
  return {
    annualCostSeries: zeroSeries,
    annualSavingsSeries: zeroSeries,
    annualNetCashFlowSeries: zeroSeries,
    cumulativeNetCostSeries: zeroSeries,
    costBreakdownByPolicy: {
      weatherization: 0,
      residentialReach: 0,
      commercialReach: 0,
      demandResponse: 0,
      electrification: 0,
      other: 0,
      incentives: 0,
    },
    costEffectivenessMetrics: {
      upfrontCapitalCost: 0,
      incrementalCapitalVsBau: 0,
      netAnnualOperatingSavings2045: 0,
      cumulativeNetCost: 0,
      netPresentValue: 0,
      costPerTonCO2eReduced: null,
      paybackYear: null,
      paybackPeriodYears: null,
    },
    totalBuildingEmissionsReductionMT: 0,
    bauBillByYear: zeroSeries,
    policyBillByYear: zeroSeries,
    bauGasCostByYear: zeroSeries,
    bauElecCostByYear: zeroSeries,
    policyGasCostByYear: zeroSeries,
    policyElecCostByYear: zeroSeries,
    annualCapitalCostSeries: zeroSeries,
  };
}

/**
 * Compute buildings cost using unified pathway-difference. Same formula for all views; only baseline and policy pathways differ.
 * When in incremental_vs_bau and there is no meaningful building emissions reduction (baseline ≈ policy), returns zeroed results.
 */
export function computeBuildingsCostResults(params: ComputeBuildingsCostResultsParams): BuildingsCostResults {
  try {
    return computeBuildingsCostResultsInner(params);
  } catch (err) {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.error('[buildingsCostModel] computeBuildingsCostResults error:', err);
    }
    const years = [2023];
    for (let y = 2024; y <= 2045; y++) years.push(y);
    return zeroedBuildingsCostResult(years);
  }
}

function computeBuildingsCostResultsInner(params: ComputeBuildingsCostResultsParams): BuildingsCostResults {
  const {
    baseline,
    policyScenario,
    efficiencyLayerOptions,
    emissionsResults,
    assumptions: assumptionOverrides,
  } = params;

  const assumptions = getAssumptions(assumptionOverrides);
  const energy = getBuildingEnergyTimeSeries(baseline, policyScenario, {
    efficiencyLayerOptions,
    cop: params.buildingsCop ?? DEFAULT_HEAT_PUMP_COP,
    rngOverridePercent: params.buildingsRngPercentOverride,
  });

  const {
    years,
    bauThermsByYear,
    bauKwhByYear,
    policyThermsByYear,
    policyKwhByYear,
    bauResThermsByYear,
    bauComThermsByYear,
    policyResThermsByYear,
    policyComThermsByYear,
  } = energy;
  const components = policyScenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();

  const referencePathway = params.referencePathway;
  const comparisonTarget = params.comparisonTarget ?? 'policy';

  // When user has no local building policy (0% electrification, 0% efficiency), return zeros immediately.
  // Prevents phantom spikes when only EV adoption slider is changed; building cost is invariant to EV.
  const hasElectrification = (policyScenario.heatPumpAdoptionPercent ?? 0) > 0;
  const hasEfficiency = hasAnyEfficiencyComponent(components);
  if (!referencePathway && !hasElectrification && !hasEfficiency) {
    return zeroedBuildingsCostResult(years);
  }

  // For incremental_vs_bau: if baseline and policy building energy (therms + kWh) are effectively identical, return zeros.
  // Uses source-of-truth energy series (never affected by EV); robust to stale emissions or floating-point noise.
  if (!referencePathway) {
    const energyIdentical =
      bauThermsByYear.length === policyThermsByYear.length &&
      bauKwhByYear.length === policyKwhByYear.length &&
      years.every((_, i) => {
        const dt = Math.abs((bauThermsByYear[i] ?? 0) - (policyThermsByYear[i] ?? 0));
        const dk = Math.abs((bauKwhByYear[i] ?? 0) - (policyKwhByYear[i] ?? 0));
        const tol = 1e-6;
        return dt < tol && dk < tol;
      });
    if (energyIdentical) {
      return zeroedBuildingsCostResult(years);
    }
  }

  let baselinePathway: BuildingsPathway;
  let policyPathway: BuildingsPathway;

  if (referencePathway) {
    baselinePathway = toBuildingsPathway(
      referencePathway.years,
      referencePathway.thermsByYear,
      referencePathway.kwhByYear,
      0,
      0
    );
    policyPathway =
      comparisonTarget === 'bau'
        ? toBuildingsPathway(years, bauThermsByYear, bauKwhByYear, 0, 0)
        : toBuildingsPathway(
            years,
            policyThermsByYear,
            policyKwhByYear,
            components.residentialReachCodePercent ?? 0,
            components.commercialReachCodePercent ?? 0
          );
  } else {
    baselinePathway = toBuildingsPathway(years, bauThermsByYear, bauKwhByYear, 0, 0);
    policyPathway = toBuildingsPathway(
      years,
      policyThermsByYear,
      policyKwhByYear,
      components.residentialReachCodePercent ?? 0,
      components.commercialReachCodePercent ?? 0
    );
  }

  // Electrification context for $/unit CAPEX: baseline vs policy res/com therms and housing
  const pop = baseline.populationData ?? {};
  const pop2023 = pop[BASE_YEAR] ?? baseline.population ?? 0;
  const housingUnitsByYear: Record<number, number> = { ...(params.housingUnitsByYear ?? {}) };
  for (const y of years) {
    if (housingUnitsByYear[y] == null || housingUnitsByYear[y] <= 0) {
      housingUnitsByYear[y] = Math.max(0, (pop[y] ?? pop2023) / 2.5);
    }
  }
  const resFracByYear = years.map((_, i) => {
    const tot = (bauResThermsByYear[i] ?? 0) + (bauComThermsByYear[i] ?? 0);
    return tot > 0 ? (bauResThermsByYear[i] ?? 0) / tot : 0.6;
  });
  const baselineResByYear: Record<number, number> = {};
  const baselineComByYear: Record<number, number> = {};
  const policyResByYear: Record<number, number> = {};
  const policyComByYear: Record<number, number> = {};
  for (let i = 0; i < years.length; i++) {
    const y = years[i];
    const idxBase = baselinePathway.years.indexOf(y);
    const idxPol = policyPathway.years.indexOf(y);
    const tBase = idxBase >= 0 ? (baselinePathway.thermsByYear[idxBase] ?? 0) : 0;
    const tPol = idxPol >= 0 ? (policyPathway.thermsByYear[idxPol] ?? 0) : 0;
    baselineResByYear[y] = tBase * resFracByYear[i];
    baselineComByYear[y] = tBase * (1 - resFracByYear[i]);
    policyResByYear[y] = tPol * resFracByYear[i];
    policyComByYear[y] = tPol * (1 - resFracByYear[i]);
  }

  const electrificationContext = {
    housingUnitsByYear,
    baselineResThermsByYear: baselineResByYear,
    policyResThermsByYear: policyResByYear,
    baselineComThermsByYear: baselineComByYear,
    policyComThermsByYear: policyComByYear,
    resHeatPumpIncrementalCostPerUnit: assumptions.resHeatPumpIncrementalCostPerUnit,
    comHeatPumpIncrementalCostPerUnit: assumptions.comHeatPumpIncrementalCostPerUnit,
    avgThermsPerResUnit: Math.max(1, assumptions.avgThermsPerResUnit),
    avgThermsPerComEquivalentUnit: Math.max(1, assumptions.avgThermsPerComEquivalentUnit),
    heatPumpRebate: assumptions.heatPumpRebate,
    hasElectrification: (policyScenario.heatPumpAdoptionPercent ?? 0) > 0,
  };

  const includeElectrificationDebug =
    typeof params.location === 'string' && params.location.toLowerCase().includes('davis');
  const pathwayResult = computeBuildingsCostBetweenPathways(
    baselinePathway,
    policyPathway,
    assumptionOverrides,
    electrificationContext,
    includeElectrificationDebug
  );

  const buildingEmissionsMT = (row: TimeSeriesEmissionData) => (row.naturalGasEmissions ?? 0) + (row.electricityEmissions ?? 0);
  const baselineTotalByYear = emissionsResults.baselineData
    .filter((r) => r.year >= BASE_YEAR && r.year <= TARGET_YEAR)
    .map((r) => ({ year: r.year, mt: buildingEmissionsMT(r) }));
  const policyTotalByYear = emissionsResults.projectedData
    .filter((r) => r.year >= BASE_YEAR && r.year <= TARGET_YEAR)
    .map((r) => ({ year: r.year, mt: buildingEmissionsMT(r) }));
  const totalReductionMT = years.reduce((sum, y) => {
    const b = baselineTotalByYear.find((r) => r.year === y)?.mt ?? 0;
    const p = policyTotalByYear.find((r) => r.year === y)?.mt ?? 0;
    return sum + Math.max(0, b - p);
  }, 0);

  const costPerTonCO2eReduced =
    totalReductionMT > 0 ? pathwayResult.cumulativeNetCost / totalReductionMT : null;
  const idx2045 = years.indexOf(TARGET_YEAR);

  const annualSavingsSeries: AnnualSeriesRow[] = pathwayResult.annualSavingsSeries.map((r) => ({ year: r.year, value: r.value }));
  const annualCapitalCostSeries: AnnualSeriesRow[] = pathwayResult.annualCapexSeries.map((r) => ({ year: r.year, value: r.value }));
  // Cash-flow sign convention for UI:
  // - Savings are positive when BAU operating cost > policy operating cost.
  // - Capital exp. is non-negative (cost outflow).
  // Net = operating impact (savings) - capex, so positive values mean net savings.
  const annualNetCashFlowSeries: AnnualSeriesRow[] = years.map((y, i) => ({
    year: y,
    value: annualSavingsSeries[i].value - annualCapitalCostSeries[i].value,
  }));
  const cumulativeNetCostSeries: AnnualSeriesRow[] = pathwayResult.cumulativeNetCostSeries.map((r) => ({ year: r.year, value: r.value }));

  const costBreakdownByPolicy: CostBreakdownByPolicy = {
    weatherization: 0,
    residentialReach: pathwayResult.reachCapex,
    commercialReach: 0,
    demandResponse: 0,
    electrification: pathwayResult.electrificationCapex,
    other: 0,
    incentives: 0,
  };

  const netAnnualOperatingSavings2045 = idx2045 >= 0 ? annualSavingsSeries[idx2045]?.value : 0;

  const costEffectivenessMetrics: CostEffectivenessMetrics = {
    upfrontCapitalCost: pathwayResult.upfrontCapitalCost,
    incrementalCapitalVsBau: pathwayResult.upfrontCapitalCost,
    netAnnualOperatingSavings2045,
    cumulativeNetCost: pathwayResult.cumulativeNetCost,
    netPresentValue: pathwayResult.netPresentValue,
    costPerTonCO2eReduced: costPerTonCO2eReduced ?? null,
    paybackYear: pathwayResult.paybackYear,
    paybackPeriodYears: pathwayResult.paybackYear != null ? pathwayResult.paybackYear - BASE_YEAR : null,
  };

  const bauBillByYear: AnnualSeriesRow[] = pathwayResult.baselineCostByYear.map((r) => ({ year: r.year, value: r.value }));
  const policyBillByYear: AnnualSeriesRow[] = pathwayResult.policyCostByYear.map((r) => ({ year: r.year, value: r.value }));

  const debug =
    idx2045 >= 0
      ? {
          year2045: {
            bauBill: bauBillByYear[idx2045]?.value ?? 0,
            policyBill: policyBillByYear[idx2045]?.value ?? 0,
            annualSavings: annualSavingsSeries[idx2045]?.value ?? 0,
            annualCapex: annualCapitalCostSeries[idx2045]?.value ?? 0,
            cumulativeNetCost: cumulativeNetCostSeries[idx2045]?.value ?? 0,
            costPerTon: costPerTonCO2eReduced,
            paybackYear: pathwayResult.paybackYear,
          },
        }
      : undefined;

  if (includeElectrificationDebug && pathwayResult.electrificationDebug && typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.log('[Building Electrification CAPEX $/unit] Davis debug:', pathwayResult.electrificationDebug);
  }

  // Required debug output per location when electrification is on
  if (hasElectrification && pathwayResult.operatingCostDebug && typeof window !== 'undefined') {
    const d = pathwayResult.operatingCostDebug;
    console.log('[Building electrification cost]', params.location, {
      baselineGasCost: d.baselineGasCostTotal,
      policyGasCost: d.policyGasCostTotal,
      policyElectricityCost: d.policyElecCostTotal,
      totalIncrementalCost: pathwayResult.cumulativeNetCost,
      electricityAddedKwh: d.electricityAddedKwhTotal,
      effectiveDollarsPerThermElectric: d.effectiveDollarsPerThermElectric,
      incrementalOperatingCost: d.incrementalOperatingCostTotal,
    });
  }

  // Berkeley cost debug: expose by-year series for troubleshooting (flat vs ramping, sign of savings).
  const isBerkeley = typeof params.location === 'string' && params.location.toLowerCase().includes('berkeley');
  if (isBerkeley && (params.policyScenario?.heatPumpAdoptionPercent ?? 0) > 0 && typeof window !== 'undefined') {
    const debugPayload = {
      scenario: { heatPumpAdoptionPercent: params.policyScenario?.heatPumpAdoptionPercent, energyEfficiencyTargetPercent: (params.policyScenario as any)?.energyEfficiencyTargetPercent },
      years,
      bauThermsByYear: [...bauThermsByYear],
      policyThermsByYear: [...policyThermsByYear],
      bauKwhByYear: [...bauKwhByYear],
      policyKwhByYear: [...policyKwhByYear],
      baselineCostByYear: pathwayResult.baselineCostByYear.map((r) => r.value),
      policyCostByYear: pathwayResult.policyCostByYear.map((r) => r.value),
      annualSavingsSeries: pathwayResult.annualSavingsSeries.map((r) => ({ year: r.year, value: r.value })),
      annualCapexSeries: pathwayResult.annualCapexSeries.map((r) => ({ year: r.year, value: r.value })),
      baselineGasCostByYear: pathwayResult.baselineGasCostByYear.map((r) => r.value),
      baselineElecCostByYear: pathwayResult.baselineElecCostByYear.map((r) => r.value),
      policyGasCostByYear: pathwayResult.policyGasCostByYear.map((r) => r.value),
      policyElecCostByYear: pathwayResult.policyElecCostByYear.map((r) => r.value),
      upfrontCapitalCost: pathwayResult.upfrontCapitalCost,
      cumulativeNetCost: pathwayResult.cumulativeNetCost,
    };
    (window as any).__BERKELEY_COST_DEBUG__ = debugPayload;
    const sav = pathwayResult.annualSavingsSeries.map((r) => r.value);
    const cap = pathwayResult.annualCapexSeries.map((r) => r.value);
    console.log('[Berkeley cost debug] Years:', years.length, '| Upfront:', pathwayResult.upfrontCapitalCost?.toFixed(0), '| CumulativeNet:', pathwayResult.cumulativeNetCost?.toFixed(0));
    console.log('[Berkeley cost debug] Annual savings (first 5 / last 5):', sav.slice(0, 5).map((v) => (v / 1e6).toFixed(2)), '/', sav.slice(-5).map((v) => (v / 1e6).toFixed(2)), '| flat?', sav.length > 1 && Math.max(...sav) - Math.min(...sav) < 1e-6 * Math.abs(sav[0]));
    console.log('[Berkeley cost debug] Annual capex (first 5 / last 5):', cap.slice(0, 5).map((v) => (v / 1e6).toFixed(2)), '/', cap.slice(-5).map((v) => (v / 1e6).toFixed(2)));
    console.log('[Berkeley cost debug] BAU elec cost 2023/2045:', (pathwayResult.baselineElecCostByYear[0]?.value / 1e6).toFixed(1), '/', (pathwayResult.baselineElecCostByYear[pathwayResult.baselineElecCostByYear.length - 1]?.value / 1e6).toFixed(1));
    console.log('[Berkeley cost debug] Policy elec cost 2023/2045:', (pathwayResult.policyElecCostByYear[0]?.value / 1e6).toFixed(1), '/', (pathwayResult.policyElecCostByYear[pathwayResult.policyElecCostByYear.length - 1]?.value / 1e6).toFixed(1));
    console.log('[Berkeley cost debug] Scenario efficiencyTargetPercent:', (params.policyScenario as any)?.energyEfficiencyTargetPercent ?? (params.policyScenario as any)?.energyEfficiencyPercent, '| hasEfficiencyComponents:', hasAnyEfficiencyComponent(params.policyScenario?.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()));
  }

  return {
    annualCostSeries: annualCapitalCostSeries.map((row) => ({ ...row, value: row.value })),
    annualSavingsSeries,
    annualNetCashFlowSeries,
    cumulativeNetCostSeries,
    costBreakdownByPolicy,
    costEffectivenessMetrics,
    totalBuildingEmissionsReductionMT: totalReductionMT,
    bauBillByYear,
    policyBillByYear,
    bauGasCostByYear: pathwayResult.baselineGasCostByYear.map((r) => ({ year: r.year, value: r.value })),
    bauElecCostByYear: pathwayResult.baselineElecCostByYear.map((r) => ({ year: r.year, value: r.value })),
    policyGasCostByYear: pathwayResult.policyGasCostByYear.map((r) => ({ year: r.year, value: r.value })),
    policyElecCostByYear: pathwayResult.policyElecCostByYear.map((r) => ({ year: r.year, value: r.value })),
    annualCapitalCostSeries,
    debug,
    electrificationDebug: pathwayResult.electrificationDebug,
    operatingCostDebug: pathwayResult.operatingCostDebug,
  };
}
